import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Handshake, MapPin } from 'lucide-react';
import { clinicApi } from '@features/clinics/services/clinicApi';
import { searchCustomerLocations, geocodeLocation, getCustomerLocationById } from '../services/customerApi';
import apiClient from '@shared/services/apiClient';
import ClientRelationshipMap from '../components/ClientRelationshipMap';
import ClientRelationshipGrid from '../components/ClientRelationshipGrid';
import ClientRelationshipPanel from '../components/ClientRelationshipPanel';
import AsyncContent from '@shared/components/common/AsyncContent';
import { useDebounce } from '@hooks/useDebounce';
import type { CustomerLocation } from '../types/clientClinic';
import type { ClinicWithDistance } from '@features/clinics/types/clinic';
import type { AccountStatus } from '../types/clientClinic';
import './ClientRelationshipsPage.css';
import { withXmId } from '@shared/utils/xmId';

function formatAddress(location: CustomerLocation): string | null {
  const parts: string[] = [];
  if (location.street) parts.push(location.street);
  if (location.suite) parts.push(location.suite);
  const cityStateZip: string[] = [];
  if (location.city) cityStateZip.push(location.city);
  if (location.state) cityStateZip.push(location.state);
  if (location.zip) cityStateZip.push(location.zip);
  if (cityStateZip.length > 0) parts.push(cityStateZip.join(', '));
  return parts.length > 0 ? parts.join(', ') : null;
}

export default function ClientRelationshipsPage() {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<CustomerLocation | null>(null);
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [selectedClinicId, setSelectedClinicId] = useState<number | undefined>(undefined);
  const [showPanel, setShowPanel] = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const handleLocationSelect = useCallback(async (location: CustomerLocation) => {
    setSelectedLocation(location);
    setSearchQuery('');
    setGeocodeError(null);
    setSelectedClinicId(undefined);
    setShowPanel(false);

    if (!location.latitude || !location.longitude) {
      try {
        const geocodedLocation = await geocodeLocation(location.customerId, location.id);
        setSelectedLocation(geocodedLocation);
      } catch (error) {
        setGeocodeError('Unable to geocode this location. Please ensure it has a valid address.');
        console.error('Geocode error:', error);
      }
    }
  }, []);

  // Check for locationId URL parameter
  useEffect(() => {
    const locationIdParam = searchParams.get('locationId');
    if (locationIdParam && !selectedLocation) {
      const locationId = parseInt(locationIdParam, 10);
      if (!isNaN(locationId)) {
        getCustomerLocationById(locationId)
          .then((location) => {
            handleLocationSelect(location);
          })
          .catch((error) => {
            console.error('Failed to load location from URL parameter:', error);
          });
      }
    }
  }, [searchParams, selectedLocation, handleLocationSelect]);

  // Search for customer locations
  const {
    data: locationResults = [],
    isLoading: isSearching,
  } = useQuery({
    queryKey: ['customerLocations', debouncedSearchQuery],
    queryFn: () => searchCustomerLocations(debouncedSearchQuery),
    enabled: debouncedSearchQuery.length >= 2,
  });

  // Fetch nearby clinics when location and radius change
  const {
    data: nearbyClinics = [],
    isLoading: isLoadingClinics,
    error: clinicsError,
    refetch: refetchClinics,
  } = useQuery({
    queryKey: ['nearbyClinics', selectedLocation?.id, radiusMiles],
    queryFn: async () => {
      if (!selectedLocation?.latitude || !selectedLocation?.longitude) {
        throw new Error('Location coordinates not available');
      }
      return clinicApi.getNearbyClinics(
        selectedLocation.latitude,
        selectedLocation.longitude,
        radiusMiles,
        50
      );
    },
    enabled: !!(selectedLocation?.latitude && selectedLocation?.longitude),
  });

  // Fetch existing relationships for this location
  const {
    data: relationships = [],
    refetch: refetchRelationships,
  } = useQuery({
    queryKey: ['clinicLocationAccounts', selectedLocation?.id],
    queryFn: async () => {
      if (!selectedLocation) return [];
      const response = await apiClient.get(`/clinic-location-client-accounts/location/${selectedLocation.id}`);
      return response.data;
    },
    enabled: !!selectedLocation,
  });

  // Build a map of clinicId -> status
  const relationshipStatuses = new Map<number, AccountStatus>();
  relationships.forEach((rel: { clinicId: number; accountStatus: AccountStatus }) => {
    relationshipStatuses.set(rel.clinicId, rel.accountStatus);
  });

  const handleClinicSelect = (clinicId: number) => {
    setSelectedClinicId(clinicId);
    setShowPanel(true);
  };

  const handlePanelClose = () => {
    setShowPanel(false);
    setSelectedClinicId(undefined);
  };

  const handleRelationshipChange = useCallback(() => {
    refetchRelationships();
    refetchClinics();
  }, [refetchRelationships, refetchClinics]);

  const selectedClinic = nearbyClinics.find((c: ClinicWithDistance) => c.id === selectedClinicId);
  const hasCoordinates = !!(selectedLocation?.latitude && selectedLocation?.longitude);

  return (
    <div className="client-relationships-page">
      {/* Search Bar (when no location selected) */}
      {!selectedLocation && (
        <>
          <div className="page-header">
            <div className="page-title-row">
              <Handshake size={28} />
              <h1>Client Relationships</h1>
            </div>
            <p className="page-description">Find nearby clinics for customer locations and manage relationships</p>
          </div>
          <div className="search-bar">
            <div className="search-input-group">
              <label htmlFor="location-search">Customer Location</label>
              <div className="autocomplete-wrapper">
                <input
                  id="location-search"
                  type="text"
                  placeholder="Search customer locations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searchQuery.length >= 2 && (
                  <div className="autocomplete-dropdown">
                    {isSearching ? (
                      <div className="autocomplete-loading">Searching...</div>
                    ) : locationResults.length > 0 ? (
                      locationResults.map((location: CustomerLocation) => (
                        <div
                          key={location.id}
                          className="autocomplete-item"
                          onClick={() => handleLocationSelect(location)}
                        >
                          <div className="autocomplete-item-name">{location.name}</div>
                          {location.city && location.state && (
                            <div className="autocomplete-item-location">
                              {location.city}, {location.state}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="autocomplete-empty">No locations found</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="empty-state">
            <div className="empty-state-icon">📍</div>
            <h2>Select a Customer Location</h2>
            <p>Search and select a customer location to find nearby clinics</p>
          </div>
        </>
      )}

      {/* Active Location View */}
      {selectedLocation && (
        <>
          {/* Top Row: info box + map */}
          <div className="top-row">
            <div className="location-info-box">
              <div className="location-info-top">
                <div>
                  {selectedLocation.customerName && (
                    <div className="location-customer-name">{withXmId(selectedLocation.customerName, selectedLocation.customerXmId)}</div>
                  )}
                  <div className="location-facility-name">{selectedLocation.name}</div>
                </div>
                <button
                  type="button"
                  className="clear-selection-btn"
                  onClick={() => {
                    setSelectedLocation(null);
                    setSearchQuery('');
                    setSelectedClinicId(undefined);
                    setShowPanel(false);
                    setGeocodeError(null);
                  }}
                >
                  Clear
                </button>
              </div>
              {formatAddress(selectedLocation) && (
                <div className="location-full-address">
                  <MapPin size={12} />
                  <span>{formatAddress(selectedLocation)}</span>
                </div>
              )}
              <div className="location-radius-control">
                <label htmlFor="radius-select">Radius:</label>
                <select
                  id="radius-select"
                  value={radiusMiles}
                  onChange={(e) => setRadiusMiles(Number(e.target.value))}
                  className="radius-select"
                >
                  <option value={5}>5 mi</option>
                  <option value={10}>10 mi</option>
                  <option value={15}>15 mi</option>
                  <option value={25}>25 mi</option>
                  <option value={50}>50 mi</option>
                </select>
              </div>
              {geocodeError && (
                <div className="geocode-error-inline">
                  <AlertCircle size={14} />
                  <span>{geocodeError}</span>
                </div>
              )}
            </div>
            {hasCoordinates && (
              <div className="top-map">
                <ClientRelationshipMap
                  customerLocation={{
                    latitude: selectedLocation.latitude!,
                    longitude: selectedLocation.longitude!,
                    name: selectedLocation.name,
                  }}
                  clinics={nearbyClinics}
                  selectedClinicId={selectedClinicId}
                  onClinicSelect={handleClinicSelect}
                  mapStyle={{ width: '280px', height: '160px', borderRadius: '6px' }}
                />
              </div>
            )}
          </div>

          {/* Work Area: grid + detail panel side by side */}
          {hasCoordinates && (
            <div className="work-area">
              <div className="grid-column">
                <AsyncContent
                  loading={isLoadingClinics}
                  error={clinicsError ? 'Error loading clinics. Please try again.' : null}
                  loadingMessage="Finding nearby clinics..."
                >
                  <div className="grid-header">
                    <h2>Nearby Clinics ({nearbyClinics.length})</h2>
                  </div>
                  <ClientRelationshipGrid
                    clinics={nearbyClinics}
                    selectedClinicId={selectedClinicId}
                    onClinicSelect={handleClinicSelect}
                    relationshipStatuses={relationshipStatuses}
                  />
                </AsyncContent>
              </div>
              <div className="detail-column">
                {showPanel && selectedClinic ? (
                  <ClientRelationshipPanel
                    clinic={selectedClinic}
                    customerLocationId={selectedLocation.id}
                    onClose={handlePanelClose}
                    onRelationshipChange={handleRelationshipChange}
                  />
                ) : (
                  <div className="detail-placeholder">
                    <p>Select a clinic to view details</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
